"use client"

import { Popover, PopoverPanel, Transition } from "@headlessui/react"
import { siteName } from "@lib/site"
import { BarsThree, XMark } from "@medusajs/icons"
import { useFrontendPreferences } from "@lib/frontend-preferences"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { Text, clx } from "@modules/common/components/ui"
import { Fragment } from "react"
import {
  FrontendLanguageSwitcher,
  FrontendThemeToggle,
} from "../frontend-preference-controls"

const SideMenu = () => {
  const { locale, t } = useFrontendPreferences()
  const drawerClosedTranslate =
    locale === "fa" ? "translate-x-full" : "-translate-x-full"

  return (
    <div className="h-full">
      <div className="flex items-center h-full">
        <Popover className="h-full flex">
          {({ open, close }) => (
            <>
              <div className="relative flex h-full">
                <Popover.Button
                  data-testid="nav-menu-button"
                  className={clx(
                    "sahil-menu-trigger relative h-full flex items-center transition-all ease-out duration-200 focus:outline-none",
                    open && "is-open"
                  )}
                >
                  {open ? (
                    <XMark className="sahil-menu-trigger__icon" aria-hidden="true" />
                  ) : (
                    <BarsThree className="sahil-menu-trigger__icon" aria-hidden="true" />
                  )}
                  <span className="sahil-menu-trigger__label">
                    {t.nav.menu}
                  </span>
                </Popover.Button>
              </div>

              {open && (
                <div
                  className="sahil-menu-backdrop fixed inset-0 z-[50]"
                  onClick={close}
                  data-testid="side-menu-backdrop"
                />
              )}

              <Transition
                show={open}
                as={Fragment}
                enter="transition-[transform,opacity] ease-out duration-300"
                enterFrom={clx("opacity-0", drawerClosedTranslate)}
                enterTo="opacity-100 translate-x-0"
                leave="transition-[transform,opacity] ease-in duration-220"
                leaveFrom="opacity-100 translate-x-0"
                leaveTo={clx("opacity-0", drawerClosedTranslate)}
              >
                <PopoverPanel
                  className={clx(
                    "sahil-menu-panel fixed inset-y-0 z-[51] w-[min(88vw,420px)] sm:w-[min(500px,88vw)]",
                    locale === "fa" ? "right-0" : "left-0"
                  )}
                >
                  <div
                    data-testid="nav-menu-popup"
                    className="sahil-menu-panel__inner"
                  >
                    <div className="sahil-menu-panel__topline">
                      <span>{t.menu.house}</span>
                      <button
                        type="button"
                        data-testid="close-menu-button"
                        onClick={close}
                        aria-label="Close menu"
                      >
                        <XMark />
                      </button>
                    </div>

                    <nav
                      aria-label="Main menu"
                      className="sahil-menu-panel__primary"
                    >
                      {t.menu.primary.map(({ name, href }) => (
                        <LocalizedClientLink
                          key={name}
                          href={href}
                          className="sahil-menu-panel__primary-link"
                          onClick={close}
                        >
                          {name}
                        </LocalizedClientLink>
                      ))}
                    </nav>

                    <div className="sahil-menu-panel__secondary">
                      <div>
                        <span>{t.menu.customerCare}</span>
                        <LocalizedClientLink href="/account" onClick={close}>
                          {t.nav.account}
                        </LocalizedClientLink>
                        <LocalizedClientLink href="/cart" onClick={close}>
                          {t.menu.shoppingBag}
                        </LocalizedClientLink>
                      </div>
                      <div>
                        <span>{t.menu.discover}</span>
                        <LocalizedClientLink href="/" onClick={close}>
                          {t.menu.ourWorld}
                        </LocalizedClientLink>
                        <LocalizedClientLink href="/store" onClick={close}>
                          {t.menu.allScarves}
                        </LocalizedClientLink>
                      </div>
                    </div>

                    <div className="sahil-menu-panel__bottom">
                      <div
                        className="sahil-menu-panel__preferences"
                        aria-label={t.menu.preferences}
                      >
                        <span>{t.menu.preferences}</span>
                        <div>
                          <FrontendLanguageSwitcher />
                          <FrontendThemeToggle />
                        </div>
                      </div>
                      <Text className="sahil-menu-panel__copyright">
                        © {new Date().getFullYear()} {siteName}
                      </Text>
                    </div>
                  </div>
                </PopoverPanel>
              </Transition>
            </>
          )}
        </Popover>
      </div>
    </div>
  )
}

export default SideMenu
